import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IPagination } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  ACTIVE_INACTIVE_SUBSCRIPTION_PACKAGE,
  DELETE_SUBSCRIPTION_PACKAGE,
  GET_ALL_SUBSCRIPTION_PACKAGE,
  GET_SUBSCRIPTION_PACKAGE_DUPLICATE_SOURCES,
} from "../../services/Endpoints";
import { ISubscriptionPackageFilter } from "../../services/subscription-package/SubscriptionPackageService";
import {
  DEFAULT_STATUS_CODE_BAD_REQUEST,
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastSuccess,
} from "../../utils/AppFunctions";
import { formatAmountWithSign } from "../../utils/PurchaseFormat";
import { getPlanTypeLabel } from "../../utils/PurchaseConstants";
import { VISIBILITY_TYPE_ENUM } from "../../utils/PurchaseEnumerations";

export interface IPackageDuplicateSource {
  id: string;
  name: string;
  visibility_type: VISIBILITY_TYPE_ENUM;
  platforms: string[];
  has_desktop: boolean;
}

export interface ISubscriptionPackageRow extends IPackageDuplicateSource {
  description: string | null;
  is_active: TBitValue;
  validity: number;
  base_price: string;
  cgst: string;
  sgst: string;
  igst: string;
  total: string;
}

export const SUBSCRIPTION_PACKAGE_FILTER_INITIAL_VALUE: ISubscriptionPackageFilter = {
  visibility_type: null,
  has_desktop: null,
  is_active: null,
};

interface ISubscriptionPackageCells {
  action: (row: ISubscriptionPackageRow) => JSX.Element;
  type: (row: ISubscriptionPackageRow) => JSX.Element;
}

export const isFreeTrialRow = (row: ISubscriptionPackageRow) =>
  row.visibility_type === VISIBILITY_TYPE_ENUM.FreeTrial;

export const getSubscriptionPackageFields = (
  cells: ISubscriptionPackageCells,
  handleStatus: (item: ISubscriptionPackageRow) => void,
  t: TUseTranslationTfn,
): ITableFields<ISubscriptionPackageRow>[] => [
  {
    name: t("name"),
    key: "name",
    sort: true,
    type: TABLE_FIELD_TYPE.text,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-3",
  },
  {
    name: t("type"),
    key: "visibility_type",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ISubscriptionPackageRow>) => cells.type(data),
  },
  {
    name: t("plan_type"),
    key: "platforms",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ISubscriptionPackageRow>) =>
      getPlanTypeLabel(t, data.platforms ?? []),
  },
  {
    name: t("validity"),
    key: "validity",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ISubscriptionPackageRow>) =>
      prepareMessageFromParams(t("validity_days_value"), [["days", String(data.validity)]]),
  },
  {
    name: t("base_price"),
    key: "base_price",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.right,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ISubscriptionPackageRow>) =>
      formatAmountWithSign(data.base_price),
  },
  {
    name: t("total"),
    key: "total",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.right,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ISubscriptionPackageRow>) => formatAmountWithSign(data.total),
  },
  {
    name: t("active"),
    key: "is_active",
    sort: false,
    type: TABLE_FIELD_TYPE.switch,
    align: TABLE_FIELD_ALIGN.center,
    checkedValue: BIT_VALUE.One,
    flex: "col-span-1",
    handleCheck: handleStatus,
    disabled: isFreeTrialRow,
  },
  {
    name: t("action"),
    key: "action",
    sort: false,
    type: TABLE_FIELD_TYPE.custom,
    align: TABLE_FIELD_ALIGN.center,
    flex: "col-span-3",
    component: cells.action,
  },
];

export const getAllSubscriptionPackage = async (
  setList: TReactSetState<ISubscriptionPackageRow[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  filter: ISubscriptionPackageFilter,
) => {
  try {
    const response = await GET_ALL_SUBSCRIPTION_PACKAGE(pagination, search, filter);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      setList(response.data.item);
      setPagination(response.data.pagination);
      return;
    }
    setList([]);
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "getAllSubscriptionPackage");
  }
};

export const getPackageDuplicateSources = async (
  setSources: TReactSetState<IPackageDuplicateSource[]>,
) => {
  try {
    const response = await GET_SUBSCRIPTION_PACKAGE_DUPLICATE_SOURCES();
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setSources(response.data ?? []);
      return;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "getPackageDuplicateSources");
  }
};

export const activeInactiveSubscriptionPackage = async (id: string, value: TBitValue) => {
  try {
    const response = await ACTIVE_INACTIVE_SUBSCRIPTION_PACKAGE(id, { is_active: value });
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      toastSuccess(response.message);
      return true;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "activeInactiveSubscriptionPackage");
  }
  return false;
};

export const deleteSubscriptionPackage = async (id: string): Promise<string | null> => {
  try {
    const response = await DELETE_SUBSCRIPTION_PACKAGE(id);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: response.message });
      return null;
    }
    if (response?.code === DEFAULT_STATUS_CODE_BAD_REQUEST && response.message) {
      return response.message;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
    return null;
  } catch (error) {
    handleError(error, "deleteSubscriptionPackage");
    return null;
  }
};
